import {
  scopeProjectRef,
  scopedThreadKey,
  scopeThreadRef,
} from "@afar-research/client-runtime/environment";
import {
  resolveThreadPullRequestChains,
  visibleThreadPullRequests,
  type ThreadPullRequestBadge,
} from "@afar-research/afar";
import type {
  SourceControlProviderInfo,
  ThreadPullRequestLink,
  VcsStatusResult,
} from "@afar-research/contracts";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CloudIcon,
  FolderGit2Icon,
  GitPullRequestIcon,
  LayersIcon,
  ListChecksIcon,
  MessageCircleQuestionIcon,
  ShieldQuestionIcon,
  TerminalIcon,
} from "lucide-react";
import { useMemo, type MouseEvent } from "react";
import { cn } from "../lib/utils";
import { showPullRequestLinkContextMenu } from "./pullRequest/pullRequestLinkContextMenu";
import { pullRequestListLines } from "./pullRequest/pullRequestListLines";
import { resolvePullRequestState } from "./pullRequest/pullRequestPresentation";
import { useEnvironment, usePrimaryEnvironmentId } from "../state/environments";
import { useProject } from "../state/entities";
import { useEnvironmentQuery } from "../state/query";
import { useThreadRunningTerminalIds } from "../state/terminalSessions";
import { vcsEnvironment } from "../state/vcs";
import { useUiStateStore } from "../uiStateStore";
import { resolveChangeRequestPresentation } from "../sourceControlPresentation";
import { resolveThreadStatusPill, type ThreadStatusPill } from "./Sidebar.logic";
import type { SidebarThreadSummary } from "../types";
import { formatWorktreePathForDisplay } from "../worktreeCleanup";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

export interface PrStatusIndicator {
  label: string;
  colorClass: string;
  tooltip: string;
  url: string;
}

export interface TerminalStatusIndicator {
  label: "Terminal process running";
  colorClass: string;
  pulse: boolean;
}

export type ThreadPr = VcsStatusResult["pr"];

export function prStatusIndicator(
  pr: ThreadPr,
  provider: VcsStatusResult["sourceControlProvider"] | null | undefined,
): PrStatusIndicator | null {
  if (!pr) return null;
  const presentation = resolveChangeRequestPresentation(provider);

  if (pr.state === "open") {
    return {
      label: `${presentation.shortName} open`,
      colorClass: "text-emerald-600 dark:text-emerald-300/90",
      tooltip: `#${pr.number} ${presentation.shortName} open: ${pr.title}`,
      url: pr.url,
    };
  }
  if (pr.state === "closed") {
    return {
      label: `${presentation.shortName} closed`,
      colorClass: "text-zinc-500 dark:text-zinc-400/80",
      tooltip: `#${pr.number} ${presentation.shortName} closed: ${pr.title}`,
      url: pr.url,
    };
  }
  if (pr.state === "merged") {
    return {
      label: `${presentation.shortName} merged`,
      colorClass: "text-violet-600 dark:text-violet-300/90",
      tooltip: `#${pr.number} ${presentation.shortName} merged: ${pr.title}`,
      url: pr.url,
    };
  }
  return null;
}

export function ChangeRequestStatusIcon({ className }: { className?: string }) {
  return <GitPullRequestIcon className={className} />;
}

export function resolveThreadPr(
  threadBranch: string | null,
  gitStatus: VcsStatusResult | null,
): ThreadPr | null {
  if (threadBranch === null || gitStatus === null || gitStatus.refName !== threadBranch) {
    return null;
  }

  return gitStatus.pr ?? null;
}

export function terminalStatusFromRunningIds(
  runningTerminalIds: ReadonlyArray<string>,
): TerminalStatusIndicator | null {
  if (runningTerminalIds.length === 0) {
    return null;
  }
  return {
    label: "Terminal process running",
    colorClass: "text-teal-600 dark:text-teal-300/90",
    pulse: true,
  };
}

export function ThreadWorktreeIndicator({
  thread,
  worktreeName,
}: {
  thread: Pick<SidebarThreadSummary, "id" | "branch" | "worktreePath">;
  worktreeName?: string | null | undefined;
}) {
  const worktreePath = thread.worktreePath?.trim();
  if (!worktreePath) {
    return null;
  }

  const displayPath = worktreeName?.trim() || formatWorktreePathForDisplay(worktreePath);
  const tooltip = worktreePath;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            aria-label={tooltip}
            data-testid={`thread-worktree-${thread.id}`}
            className="inline-flex max-w-32 items-center gap-1 rounded-md border border-border/60 bg-muted/50 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground"
          />
        }
      >
        <FolderGit2Icon className="size-2.5 shrink-0 text-muted-foreground/60" />
        <span className="truncate">{displayPath}</span>
      </TooltipTrigger>
      <TooltipPopup side="top">{tooltip}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * Every status carries its own icon, so a row says what state it is in without
 * relying on the label's color alone.
 */
function ThreadStatusIcon({ label }: { label: ThreadStatusPill["label"] }) {
  const className = "size-4 shrink-0";
  switch (label) {
    case "Working":
      return <CircleDashedIcon aria-hidden className={className} />;
    case "Input":
      return <MessageCircleQuestionIcon aria-hidden className={className} />;
    case "Approval":
      return <ShieldQuestionIcon aria-hidden className={className} />;
    case "Failed":
      return <CircleAlertIcon aria-hidden className={className} />;
    case "Plan Ready":
      return <ListChecksIcon aria-hidden className={className} />;
    case "Done":
      return <CircleCheckIcon aria-hidden className={className} />;
  }
}

export function ThreadStatusLabel({
  status,
  compact = false,
}: {
  status: ThreadStatusPill;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              aria-label={status.label}
              className={`inline-flex size-3.5 shrink-0 items-center justify-center ${status.colorClass}`}
            />
          }
        >
          <span className={`size-[9px] rounded-full ${status.dotClass}`} />
        </TooltipTrigger>
        <TooltipPopup side="top">{status.label}</TooltipPopup>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            aria-label={status.label}
            className={`inline-flex items-center gap-1 text-xs font-medium ${status.colorClass}`}
          />
        }
      >
        <ThreadStatusIcon label={status.label} />
        <span role="status">{status.label}</span>
      </TooltipTrigger>
      <TooltipPopup side="top">{status.label}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * Non-interactive leading status icons for a thread row in compact contexts
 * like the command palette. Shows the change request state icon (if present) and the
 * thread status dot, matching the sidebar's leading indicators.
 */
export function ThreadRowLeadingStatus({ thread }: { thread: SidebarThreadSummary }) {
  const threadRef = scopeThreadRef(thread.environmentId, thread.id);
  const lastVisitedAt = useUiStateStore(
    (state) => state.threadLastVisitedAtById[scopedThreadKey(threadRef)],
  );
  const threadProject = useProject(
    useMemo(
      () => scopeProjectRef(thread.environmentId, thread.projectId),
      [thread.environmentId, thread.projectId],
    ),
  );
  const threadProjectCwd = threadProject?.workspaceRoot ?? null;
  const gitCwd = thread.worktreePath ?? threadProjectCwd;
  const gitStatus = useEnvironmentQuery(
    thread.branch != null && gitCwd !== null
      ? vcsEnvironment.status({
          environmentId: thread.environmentId,
          input: { cwd: gitCwd },
        })
      : null,
  );
  const pr = resolveThreadPr(thread.branch, gitStatus.data);
  const prStatus = prStatusIndicator(pr, gitStatus.data?.sourceControlProvider);
  const threadStatus = resolveThreadStatusPill({
    thread: {
      ...thread,
      lastVisitedAt,
    },
  });

  if (!prStatus && !threadStatus) {
    return null;
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      {prStatus ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                aria-label={prStatus.tooltip}
                className={`inline-flex items-center justify-center ${prStatus.colorClass}`}
              />
            }
          >
            <ChangeRequestStatusIcon className="size-3" />
          </TooltipTrigger>
          <TooltipPopup side="top">{prStatus.tooltip}</TooltipPopup>
        </Tooltip>
      ) : null}
      {threadStatus ? <ThreadStatusLabel status={threadStatus} /> : null}
    </span>
  );
}

/**
 * Non-interactive trailing status icons for a thread row in compact contexts
 * like the command palette. Shows a terminal-running indicator and a remote
 * environment indicator, matching the sidebar's trailing indicators.
 */
export function ThreadRowTrailingStatus({ thread }: { thread: SidebarThreadSummary }) {
  const runningTerminalIds = useThreadRunningTerminalIds({
    environmentId: thread.environmentId,
    threadId: thread.id,
  });
  const environment = useEnvironment(thread.environmentId);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const isRemoteThread =
    primaryEnvironmentId !== null && thread.environmentId !== primaryEnvironmentId;
  const remoteEnvLabel = environment?.label ?? null;
  const threadEnvironmentLabel = isRemoteThread ? (remoteEnvLabel ?? "Remote") : null;
  const terminalStatus = terminalStatusFromRunningIds(runningTerminalIds);

  if (!terminalStatus && !isRemoteThread) {
    return null;
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      {terminalStatus ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                role="img"
                aria-label={terminalStatus.label}
                className={`inline-flex items-center justify-center ${terminalStatus.colorClass}`}
              />
            }
          >
            <TerminalIcon
              className={`size-3 ${terminalStatus.pulse ? "animate-status-pulse" : ""}`}
            />
          </TooltipTrigger>
          <TooltipPopup side="top">{terminalStatus.label}</TooltipPopup>
        </Tooltip>
      ) : null}
      {isRemoteThread ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                aria-label={threadEnvironmentLabel ?? "Remote"}
                className="inline-flex items-center justify-center"
              />
            }
          >
            <CloudIcon className="size-3 text-muted-foreground/60" />
          </TooltipTrigger>
          <TooltipPopup side="top">{threadEnvironmentLabel}</TooltipPopup>
        </Tooltip>
      ) : null}
    </span>
  );
}

export { resolveThreadPullRequestBadge, type ThreadPullRequestBadge } from "@afar-research/afar";

/**
 * A thread's own collection already carries what the host said, so a badge reads it directly
 * instead of leasing a summary request per row. Only a thread with no collection falls back to
 * the branch's pull request.
 */
export function linkedThreadPullRequestStatus(link: ThreadPullRequestLink): {
  readonly pr: NonNullable<ThreadPr>;
  readonly sourceControlProvider: SourceControlProviderInfo;
} | null {
  const observation = link.observation;
  if (observation === null) return null;
  return {
    pr: {
      number: link.number,
      url: link.url,
      title: observation.title,
      state: observation.state,
      headRef: observation.headBranch,
      baseRef: observation.baseBranch,
    },
    sourceControlProvider: { kind: "github", name: "GitHub", baseUrl: "" },
  };
}

/** The ink each aggregate state wears, shared by the number badge and the stack badge. */
const PR_STATE_COLOR_CLASS: Record<ThreadPullRequestBadge["state"], string> = {
  open: "text-emerald-600 dark:text-emerald-300/90",
  merged: "text-violet-600 dark:text-violet-300/90",
  closed: "text-red-600 dark:text-red-300/90",
  draft: "text-zinc-500 dark:text-zinc-400/80",
};

/**
 * The linked-pull-request control a thread row wears. A stack opens the panel, because no single
 * pull request stands for it; anything else addresses the one request the badge counts from.
 */
export function ThreadPullRequestBadgeControl({
  badge,
  number,
  url,
  status,
  pullRequests,
  onOpenStack,
  onOpenPullRequest,
}: {
  badge: ThreadPullRequestBadge | null;
  number?: number | undefined;
  url?: string | undefined;
  status: PrStatusIndicator | null;
  /** The collection behind the badge, so a hover can answer what is in it. */
  pullRequests?: ReadonlyArray<ThreadPullRequestLink> | undefined;
  onOpenStack: () => void;
  onOpenPullRequest: (event: MouseEvent<HTMLElement>) => void;
}) {
  const isStack = badge?.kind === "stack";
  const linkedCount = badge?.kind === "pull-request" && badge.others > 0 ? badge.others + 1 : null;
  if (!isStack && (number === undefined || url === undefined)) return null;
  const label = isStack
    ? `Stack of ${badge.layers} pull requests, ${badge.state}`
    : `${status?.tooltip ?? `Pull request #${number}, state not read yet`}${
        badge?.kind === "pull-request" && badge.others > 0
          ? `, and ${badge.others} more linked; ${badge.state} overall`
          : ""
      }`;
  const className = cn(
    "inline-flex shrink-0 cursor-pointer items-center gap-0.5 text-[10px] tabular-nums",
    badge !== null && (isStack || linkedCount !== null)
      ? PR_STATE_COLOR_CLASS[badge.state]
      : (status?.colorClass ?? "text-muted-foreground"),
  );
  const Glyph = isStack ? LayersIcon : GitPullRequestIcon;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // A stack is a button because no single address stands for it. One pull request stays
          // a real anchor, so a middle-click, "open in new tab" and "copy link address" keep
          // working, and right-clicking it offers copy before the platform's edit menu does.
          isStack ? (
            <button
              type="button"
              aria-label={label}
              className={className}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenStack();
              }}
            />
          ) : (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className={className}
              onPointerDown={(event) => event.stopPropagation()}
              onContextMenu={(event) => {
                if (url === undefined) return;
                event.preventDefault();
                void showPullRequestLinkContextMenu({
                  url,
                  position: { x: event.clientX, y: event.clientY },
                });
              }}
              onClick={onOpenPullRequest}
            />
          )
        }
      >
        <Glyph aria-hidden className="size-3 shrink-0" />
        {isStack ? badge.layers : linkedCount !== null ? `+${linkedCount}` : number}
      </TooltipTrigger>
      <TooltipPopup side="top">
        {/* One pull request is fully described by the label; a collection is not, so the hover
            shows what is in it rather than only how many. */}
        {visibleThreadPullRequests(pullRequests ?? []).length > 1 ? (
          <span className="flex flex-col gap-1">
            <span>{label}</span>
            <ThreadPullRequestsMiniList pullRequests={pullRequests ?? []} />
          </span>
        ) : (
          label
        )}
      </TooltipPopup>
    </Tooltip>
  );
}

/**
 * A miniature of the pull requests panel for a row's tooltip: same order and same indent, so a
 * hover answers what is in the collection without opening the surface.
 */
export function ThreadPullRequestsMiniList({
  pullRequests,
}: {
  pullRequests: ReadonlyArray<ThreadPullRequestLink>;
}) {
  const lines = useMemo(
    () =>
      pullRequestListLines(resolveThreadPullRequestChains(visibleThreadPullRequests(pullRequests))),
    [pullRequests],
  );
  if (lines.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {lines.map((line) => {
        const observation = line.link.observation;
        const presentation =
          observation === null
            ? null
            : resolvePullRequestState({ state: observation.state, isDraft: observation.isDraft });
        return (
          <li
            key={`${line.link.host}/${line.link.repository}#${line.link.number}`}
            className="flex min-w-0 items-center gap-2"
            // Capped like the panel: past a few layers the indent only repeats "still in the
            // stack", and a long stack would walk its titles off the popover.
            style={{ paddingLeft: `${Math.min(line.depth, 3) * 0.75}rem` }}
          >
            {presentation ? (
              <presentation.Icon
                aria-hidden
                className={cn("size-3 shrink-0", presentation.toneClassName)}
              />
            ) : (
              <GitPullRequestIcon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
            )}
            <span className="shrink-0 font-mono tabular-nums">#{line.link.number}</span>
            <span className="min-w-0 truncate text-foreground/75">
              {observation?.title ?? line.link.repository}
            </span>
            {line.stack ? (
              <span className="ml-auto shrink-0 pl-1 text-[10px]">
                {line.stack.kind === "native" ? "stack" : "chain"} · {line.stack.size}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
